import { Box, Button, Heading, Text, ContainerProps } from '@everlywell/ui-kit';
import SectionContainer from 'components/PageSections/components/SectionContainer';
import { Link } from 'gatsby';
import { GatsbyImage, getSrc } from 'gatsby-plugin-image';
import React from 'react';
import { useInView } from 'react-intersection-observer';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { replaceRedirectionLinkRoot } from 'utils/helpers/replaceRedirectionLinkRoot';
import { CallToAction, LongText, Media } from 'utils/types/contentfulTypes';

export type CtaBannerProps = {
  title?: string;
  description?: LongText;
  cta?: CallToAction;
  media?: Media;
  backgroundColor?: string;
  textColor?: string;
  primaryColor?: string;
  useBackgroundImage?: boolean;
  sx?: any;
  slots?: CtaBannerSlotOverrides;
};

type SlotOverride<TProps = Record<string, unknown>> = {
  sx?: any;
  props?: Partial<TProps>;
};

export type CtaBannerSlotOverrides = {
  sectionContainer?: SlotOverride<ContainerProps>;
  root?: SlotOverride;
  contentWrapper?: SlotOverride;
  textColumn?: SlotOverride;
  textInner?: SlotOverride;
  title?: SlotOverride;
  description?: SlotOverride;
  mobileImageContainer?: SlotOverride;
  mobileImage?: SlotOverride;
  desktopImageContainer?: SlotOverride;
  desktopImage?: SlotOverride;
  buttonContainer?: SlotOverride;
  button?: SlotOverride;
};

/**
 * CtaBanner is a component that displays banner  with a title, description, and a call to action button.
 * It also accepts theme, where you can customize the color scheme.
 */
export default function CtaBanner({
  title,
  description,
  cta,
  media,
  backgroundColor,
  textColor,
  primaryColor,
  useBackgroundImage,
  sx,
  slots,
}: CtaBannerProps): JSX.Element {
  const [sectionRef, isSectionInView] = useInView({ triggerOnce: true });
  const { desktopImage, mobileImage, desktopImageAltText, mobileImageAltText } =
    media || {};

  const backgroundImage =
    useBackgroundImage && isSectionInView && desktopImage && mobileImage
      ? [`url(${getSrc(mobileImage)})`, null, `url(${getSrc(desktopImage)})`]
      : undefined;

  const isCtaFullUrl = cta?.url?.includes('http');

  const onCtaClick = () => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        sectionHeader: title ?? '',
        contentfulSection: 'Cta Banner',
        ctaText: cta?.label ?? '',
      },
    });
  };
  const sectionContainerSlot = slots?.sectionContainer;
  const {
    size: sectionContainerSize,
    sx: _sectionContainerSx,
    ...sectionContainerProps
  } = ((sectionContainerSlot?.props ?? {}) as Partial<ContainerProps>);

  const defaultSectionContainerSize: ContainerProps['size'] = [
    'fluid',
    'fluid',
    'fluid',
    'fluid',
    'ultra',
  ];

  const sectionContainerBaseProps: Partial<ContainerProps> = {
    size: defaultSectionContainerSize,
    justifyContent: 'space-between',
    backgroundColor: backgroundColor ?? 'transparent',
    flexDirection: ['column', 'column', 'row'],
    marginY: [0, 0, useBackgroundImage ? 0 : 6],
    centerContent: true,
    backgroundImage,
    backgroundSize: useBackgroundImage ? ['contain', 'contain', 'auto 100%'] : 'cover',
    backgroundPosition: useBackgroundImage
      ? ['center bottom', 'center bottom', 'right center']
      : ['100% 0%', '100% 60%', '100% 0%'],
    backgroundRepeat: 'no-repeat',
    paddingBottom: useBackgroundImage ? ['70%', '60%', 0] : undefined,
  };

  const mergedSectionContainerProps = {
    ...sectionContainerBaseProps,
    ...sectionContainerProps,
    size: sectionContainerSize ?? sectionContainerBaseProps.size,
  } as ContainerProps;

  const sectionSx = {
    ...(sx || {}),
    ...(sectionContainerSlot?.sx || {}),
    ...(slots?.root?.sx || {}),
  };

  return (
    <SectionContainer
      ref={sectionRef}
      {...mergedSectionContainerProps}
      sx={sectionSx}
    >
      <Box
        display="flex"
        flexDirection={['column', 'column', 'row']}
        width={['100%', '100%', '45%']}
        sx={slots?.contentWrapper?.sx}
      >
        <Box
          marginTop={[12]}
          padding={[6, 6, 12, 16]}
          flex={['unset', 'unset', 1]}
          display="flex"
          flexDirection="column"
          alignItems={['center', 'center', 'flex-start']}
          justifyContent="center"
          sx={slots?.textColumn?.sx}
        >
          <Box
            marginTop={[6, 6, 0]}
            marginBottom={[6, 6, 0]}
            display="flex"
            flexDirection={['column']}
            alignItems={['center', 'center', 'flex-start']}
            sx={slots?.textInner?.sx}
          >
            {title ? (
              <Heading
                // @ts-ignore
                size={['32px', '2xl']}
                as="h2"
                marginBottom={3}
                textAlign={['center', 'center', 'left']}
                color={textColor ? textColor : 'tints.black'}
                sx={slots?.title?.sx}
              >
                {title}
              </Heading>
            ) : null}

            {description?.description ? (
              <Text
                size="xl"
                marginBottom={[6, 10]}
                textAlign={['center', 'left']}
                color={textColor ? textColor : 'tints.black'}
                sx={slots?.description?.sx}
              >
                {description.description}
              </Text>
            ) : null}

            {cta?.label && cta?.url && (
              <Box sx={slots?.buttonContainer?.sx}>
                {isCtaFullUrl ? (
                  <Button
                    sx={slots?.button?.sx}
                    variant="primary"
                    as="a"
                    href={replaceRedirectionLinkRoot(cta.url)}
                    onClick={() => onCtaClick()}
                  >
                    {cta.label}
                  </Button>
                ) : (
                  <Button
                    sx={slots?.button?.sx}
                    variant="primary"
                    as={Link}
                    to={cta.url}
                    onClick={onCtaClick}
                  >
                    {cta.label}
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {mobileImage && !useBackgroundImage ? (
          <Box
            display={['flex', 'flex', 'none']}
            width="100%"
            height="450px"
            flex={['unset', 'unset', 1]}
            justifyContent="end"
            sx={slots?.mobileImageContainer?.sx}
          >
            <Box sx={slots?.mobileImage?.sx} width="100%">
              <GatsbyImage
                image={mobileImage.gatsbyImageData}
                alt={mobileImageAltText || ''}
                imgStyle={{ objectPosition: 'top' }}
              />
            </Box>
          </Box>
        ) : null}
      </Box>
      {desktopImage && !useBackgroundImage ? (
        <Box
          display={['none', 'none', 'block']}
          flex={['unset', 'unset', 1]}
          maxHeight="650"
          maxWidth="450"
          overflow="hidden"
          sx={slots?.desktopImageContainer?.sx}
        >
          <Box sx={slots?.desktopImage?.sx} height="100%">
            <GatsbyImage
              image={desktopImage.gatsbyImageData}
              alt={desktopImageAltText || ''}
              style={{ height: '100%' }}
              imgStyle={{ objectPosition: 'top' }}
            />
          </Box>
        </Box>
      ) : null}
    </SectionContainer>
  );
}
